import { usePage } from '@inertiajs/vue3';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

export type NavDirection = 'forward' | 'backward';

/**
 * Tracks navigation depth (0 = home, 1 = tracker, 2 = hunt) and exposes a
 * direction ref that updates synchronously before reactive v-if conditions
 * change, so Transition components receive the correct name on every navigation.
 */
export function useNavDirection() {
    const page = usePage();

    const depth = computed(() => {
        const props = page.props as Record<string, any>;
        if (props.hunt) return 2;
        if (props.tracker) return 1;
        return 0;
    });

    const direction = ref<NavDirection>('forward');
    let prevDepth = depth.value;

    watch(
        depth,
        (newDepth) => {
            direction.value = newDepth >= prevDepth ? 'forward' : 'backward';
            prevDepth = newDepth;
        },
        { flush: 'sync' },
    );

    return { direction };
}

/**
 * Reactively tracks whether the viewport is at or above the xl breakpoint
 * (1280px). Used to keep panels in the DOM on desktop even when they would
 * otherwise be hidden on mobile.
 */
export function useIsDesktop() {
    const isDesktop = ref(typeof window !== 'undefined' ? window.matchMedia('(min-width: 1280px)').matches : false);
    let mq: MediaQueryList | undefined;

    const update = () => {
        if (mq) {
            isDesktop.value = mq.matches;
        }
    };

    onMounted(() => {
        mq = window.matchMedia('(min-width: 1280px)');
        isDesktop.value = mq.matches;
        mq.addEventListener('change', update);
    });

    onUnmounted(() => {
        mq?.removeEventListener('change', update);
    });

    return isDesktop;
}
